import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, User, ArrowRight, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import SEOHead from "../components/SEOHead";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    let cancelled = false;
    setStatus("loading"); setPost(null);
    Promise.all([
      api.get(`/blog/${slug}`).then(r => r.data),
      api.get("/blog").then(r => r.data).catch(()=>[]),
    ]).then(([p, all]) => {
      if (cancelled) return;
      setPost(p);
      setRelated(all.filter(x => x.slug !== p.slug).slice(0, 3));
      setStatus("ok");
    }).catch(()=>{ if (!cancelled) setStatus("notfound"); });
    return () => { cancelled = true; };
  }, [slug]);

  if (status === "loading") {
    return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-blue-400" size={28}/></div>;
  }

  if (status === "notfound" || !post) {
    return (
      <div className="section text-center">
        <h1 className="text-4xl font-extrabold text-white" style={{fontFamily:'Outfit'}}>Post not found</h1>
        <Link to="/blog" className="btn-primary mt-6 inline-flex">Back to blog</Link>
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "image": [post.cover],
    "datePublished": post.created_at || post.date,
    "author": {"@type": "Organization", "name": post.author || "Optivize Digital"},
    "publisher": {"@type": "Organization", "name": "Optivize Digital", "url": "https://optivize.digital"},
    "description": post.excerpt,
    "articleSection": post.category,
  };

  return (
    <div>
      <SEOHead
        title={`${post.title} — Optivize Digital`}
        description={post.excerpt}
        image={post.cover}
        schema={articleSchema}
      />
      <section className="relative">
        <div className="aspect-[16/7] sm:aspect-[16/6] lg:aspect-[16/5] w-full overflow-hidden relative">
          <img src={post.cover} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent" />
        </div>
        <div className="max-w-3xl mx-auto px-5 -mt-32 relative z-10">
          <span className="tag !bg-blue-600 !text-white !border-blue-600 mb-4">{post.category}</span>
          <motion.h1
            initial={{opacity:0, y:20}} animate={{opacity:1, y:0}} transition={{duration:.5}}
            className="text-3xl sm:text-5xl font-extrabold text-white"
            style={{fontFamily:'Outfit'}}
            data-testid="post-title"
          >
            {post.title}
          </motion.h1>
          <div className="flex items-center gap-4 mt-5 text-sm text-slate-400 flex-wrap">
            <span className="inline-flex items-center gap-1.5"><User size={14}/> {post.author}</span>
            <span className="inline-flex items-center gap-1.5"><Calendar size={14}/> {post.date}</span>
            <span className="inline-flex items-center gap-1.5"><Clock size={14}/> {post.read} read</span>
          </div>
        </div>
      </section>

      <article className="py-12 px-5">
        <div className="max-w-3xl mx-auto space-y-5 text-slate-200 leading-relaxed text-[17px]" data-testid="post-body">
          {(post.body || []).map((block, i) => {
            if (block.h2) return (
              <h2 key={i} className="text-2xl sm:text-3xl font-extrabold text-white pt-6" style={{fontFamily:'Outfit'}}>{block.h2}</h2>
            );
            if (block.text) return <p key={i}>{block.text}</p>;
            return null;
          })}
          <div className="border-t border-slate-800 mt-12 pt-8">
            <Link to="/blog" className="btn-ghost inline-flex"><ArrowLeft size={14}/> Back to blog</Link>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="py-16 px-5" style={{background:'var(--bg-section)'}}>
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-extrabold text-white mb-8" style={{fontFamily:'Outfit'}}>You might also like</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="card overflow-hidden block group">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={p.cover} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy"/>
                  </div>
                  <div className="p-5">
                    <span className="text-xs uppercase tracking-wider text-blue-400 font-semibold">{p.category}</span>
                    <h3 className="text-white font-bold mt-2" style={{fontFamily:'Outfit'}}>{p.title}</h3>
                    <div className="mt-3 text-blue-400 text-sm inline-flex items-center gap-1">Read <ArrowRight size={14}/></div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
